import { GetTransactionDto } from "@api/api";
import "./grid-transaction.scss";

import { DataView } from "primereact/dataview";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { flatMap } from "lodash-es";
import { useTransactionHelperContext } from "../transaction-helper/transaction-helper.context";
import { ExtendedService } from "@core/model/extended-service.interface";
import { useTransactionUtil } from "@core/utility/transaction.helper";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { BookingEnum } from "@core/model/booking-enum";
import classNames from "classnames";

import { useNavigate } from "react-router-dom";
import { getAgeFromBirthday } from "@shared/utility/date-time-service/birthday";
import { useMemo } from "react";

/* eslint-disable-next-line */
export interface GridTransactionProps {
  transactions: GetTransactionDto[];
  search?: string;
  rowLimit: number;
  first: number;
  totalRecords?: number;
  onPageChange?(event: PaginatorPageChangeEvent): void;
}

export function GridTransaction({
  transactions,
  search,
  rowLimit,
  first,
  totalRecords,
  onPageChange,
}: GridTransactionProps) {
  const navigate = useNavigate();
  const { formatTimeLocal } = useDateTimeContext();
  const { combinedServices, expandedService, timeLeft } =
    useTransactionHelperContext();
  const { getEndDateTime, getServiceStart, getTimerClassName, sortServicesByExpiration } =
    useTransactionUtil();
  const flatTransaction = useMemo(() => {
    return flatMap(
      transactions.map((item) => expandedService(item))
    ) as ExtendedService[];
  }, [transactions, expandedService]);

  const booking = useMemo(() => {
    return combinedServices(flatTransaction) as ExtendedService[];
  }, [flatTransaction, combinedServices]);

  const bookingDisplayRaw = useMemo(() => {
    const baseList = booking.length > 0 ? booking : flatTransaction;
    return sortServicesByExpiration(baseList);
  }, [booking, flatTransaction, sortServicesByExpiration]);

  const bookingDisplay = useMemo(() => {
    if (!search) {
      return bookingDisplayRaw;
    }
    const cleanSearch = search.trim().toLowerCase();
    if (!cleanSearch) {
      return bookingDisplayRaw;
    }
    return bookingDisplayRaw.filter((item) => {
      const childName = `${item.first_name ?? ""} ${item.last_name ?? ""}`.toLowerCase();
      const parentName = `${item.parent_first_name ?? ""} ${item.parent_last_name ?? ""}`.toLowerCase();
      const serviceName = (item.service_name ?? "").toLowerCase();
      const mobileNumber = (item.mobile_number ?? "").toLowerCase();
      const transactionCode = (item.transactionCode ?? "").toLowerCase();

      return (
        childName.includes(cleanSearch) ||
        parentName.includes(cleanSearch) ||
        serviceName.includes(cleanSearch) ||
        mobileNumber.includes(cleanSearch) ||
        transactionCode.includes(cleanSearch)
      );
    });
  }, [bookingDisplayRaw, search]);

  const navigateToRecord = (id: string) => {
    navigate(`${id}`);
  };
  const grid = (service: ExtendedService) => {
    const end = getEndDateTime(service);
    const start = getServiceStart(service);
    const startTime = start ? formatTimeLocal(start) : null;
    const endTime = end ? formatTimeLocal(end) : null;
    const displayTimer = service.status === BookingEnum.Ongoing;
    const expiryNotice = (service.member ? timeLeft[service.member] : null) || timeLeft[service.code] || "Calculating";
    const age = service.birthday
      ? getAgeFromBirthday(service.birthday).toString()
      : "-";
    const gender = service.gender ?? "-";

    return (
      <section
        className="gt-card"
        onClick={() => navigateToRecord(service.transactionCode)}
      >
        {/* Row 1: Name (big) ←→ Age, Gender */}
        <div className="gt-row gt-row--top">
          <h4 className="gt-name">
            {service.first_name} {service.last_name}
          </h4>
          <span className="gt-age-gender">
            {age}, {gender.charAt(0).toUpperCase()}
          </span>
        </div>

        {/* Row 2: Start ←→ Paid/Unpaid */}
        <div className="gt-row">
          <span className="gt-start">
            Start: {startTime ?? "-"}
          </span>
          <span className={`gt-paid${service.is_paid ? " gt-paid--paid" : " gt-paid--unpaid"}`}>
            {service.is_paid ? "Paid" : "Unpaid"}
          </span>
        </div>

        {/* Row 3: END (big) */}
        {endTime && !service.is_open_time ? (
          <div className="gt-row">
            <h3 className="gt-end">END: {endTime}</h3>
          </div>
        ) : (
          <div className="gt-row">
            <span className="gt-open-time">Open Time</span>
          </div>
        )}

        {/* Row 4: Duration */}
        {displayTimer && (
          <div className="gt-row">
            <span
              className={classNames(
                "gt-duration",
                getTimerClassName(expiryNotice)
              )}
            >
              Duration: {expiryNotice}
            </span>
          </div>
        )}

      </section>
    );
  };
  const itemTemplate = (campaign: ExtendedService) => {
    if (!campaign) {
      return;
    }

    return <div key={campaign.code}>{grid(campaign)}</div>;
  };
  const listTemplate = (campaigns: ExtendedService[]): any => {
    return (
      <div
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 p-4`}
      >
        {campaigns.map((campaign) => itemTemplate(campaign))}
      </div>
    );
  };

  return (
    <div id="GridTransaction" className="grid-transaction">
      <DataView
        value={bookingDisplay}
        listTemplate={listTemplate}
        layout={"grid"}
      />
      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={totalRecords}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </div>
  );
}

export default GridTransaction;
